import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { fetchGuests, inviteGuest, revokeGuest } from '@/features/access/api'
import type { ApiError } from '@/lib/api/client'
import type { Guest, GuestInvite } from '@/types/work'

/**
 * Granting a client access to exactly this project, and taking it back.
 *
 * The panel lives on the board because guest access is always project-scoped —
 * there is no organisation-wide guest.
 */
export function ProjectGuestsPanel({ projectKey }: { projectKey: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [days, setDays] = useState(30)
  const [issued, setIssued] = useState<GuestInvite | null>(null)

  const guests = useQuery<Guest[], ApiError>({
    queryKey: ['guests', projectKey],
    queryFn: () => fetchGuests(projectKey),
    retry: false,
  })

  const invite = useMutation<GuestInvite, ApiError, void>({
    mutationFn: () => inviteGuest(projectKey, { email, expiresInDays: days }),
    onSuccess: (data) => {
      setIssued(data)
      setEmail('')
      void queryClient.invalidateQueries({ queryKey: ['guests', projectKey] })
    },
  })

  const revoke = useMutation<void, ApiError, string>({
    mutationFn: (membershipId) => revokeGuest(projectKey, membershipId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['guests', projectKey] }),
  })

  if (guests.isError) {
    // Contributors and guests cannot manage access; the panel simply is not
    // theirs to see.
    return (
      <p className="border-line bg-surface text-ink-500 mb-6 rounded-md border px-4 py-3 text-sm">
        {t('access.notAllowed')}
      </p>
    )
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    invite.mutate()
  }

  return (
    <div className="border-line bg-surface mb-6 rounded-md border p-4">
      <h3 className="text-ink-900 mb-1 text-sm font-semibold">{t('access.guestAccess')}</h3>
      <p className="text-ink-500 mb-4 text-xs">{t('access.guestExplainer')}</p>

      <form onSubmit={submit} className="mb-4 flex flex-wrap items-end gap-2">
        <label className="min-w-56 flex-1">
          <span className="text-ink-700 mb-1 block text-xs font-medium">{t('access.email')}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="contact@client.example"
            className="border-line focus:border-brand-600 w-full rounded-md border px-3 py-1.5 text-sm outline-none"
          />
        </label>

        <label>
          <span className="text-ink-700 mb-1 block text-xs font-medium">{t('access.expiresIn')}</span>
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="border-line bg-surface rounded-md border px-3 py-1.5 text-sm"
          >
            {[7, 30, 90, 180].map((option) => (
              <option key={option} value={option}>
                {option} {t('access.days')}
              </option>
            ))}
          </select>
        </label>

        <button
          type="submit"
          disabled={invite.isPending}
          className="bg-brand-600 hover:bg-brand-700 rounded-md px-4 py-1.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {t('access.invite')}
        </button>
      </form>

      {invite.isError ? (
        <p className="mb-3 text-sm text-[color:var(--color-state-blocked)]">
          {invite.error.errors?.email?.[0] ?? t('access.inviteFailed')}
        </p>
      ) : null}

      {issued ? (
        <div className="border-brand-300 bg-brand-50 mb-4 rounded border px-3 py-2">
          <p className="text-brand-700 text-xs">{t('access.linkIssued')}</p>
          <code className="text-ink-700 mt-1 block break-all font-mono text-[11px]">
            {issued.acceptUrl}
          </code>
        </div>
      ) : null}

      {guests.isPending ? (
        <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
      ) : (guests.data ?? []).length === 0 ? (
        <p className="text-ink-500 text-sm">{t('access.noGuests')}</p>
      ) : (
        <ul className="border-line divide-line divide-y rounded border">
          {(guests.data ?? []).map((guest) => (
            <li key={guest.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-ink-900 truncate text-sm">
                  {guest.user?.name ?? t('access.pending')}
                </div>
                <div className="text-ink-500 truncate text-xs">{guest.user?.email}</div>
              </div>

              <span
                className={`shrink-0 rounded border px-2 py-0.5 font-mono text-[11px] ${
                  guest.hasExpired
                    ? 'border-[color:var(--color-state-blocked)] text-[color:var(--color-state-blocked)]'
                    : 'border-line text-ink-500'
                }`}
              >
                {guest.hasExpired
                  ? t('access.expired')
                  : guest.expiresAt === null
                    ? '—'
                    : new Date(guest.expiresAt).toISOString().slice(0, 10)}
              </span>

              <button
                type="button"
                onClick={() => revoke.mutate(guest.id)}
                disabled={revoke.isPending}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] shrink-0 text-xs underline"
              >
                {t('access.revoke')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
